import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recordsApi } from '../api/records';
import { adminApi } from '../api/admin';
import { tagsApi } from '../api/tags';
import toast from 'react-hot-toast';
import { Plus, Search, ChevronRight, ChevronUp, ChevronDown, X, Tag, Filter, ChevronLeft } from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/timezone';
import { useAuthStore } from '../store/authStore';

export default function RecordList() {
  const { user: currentUser } = useAuthStore();
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [listFields, setListFields] = useState([]);
  const [recordType, setRecordType] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [stageFilter, setStageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState('');
  const [recordTags, setRecordTags] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const rtRes = await adminApi.getRecordTypes();
      const rt = rtRes.data.record_types.find(r => r.slug === typeSlug);
      setRecordType(rt);

      const res = await recordsApi.list({
        type: typeSlug, page, per_page: 25,
        search: search || undefined,
        workflow_id: (workflows.length > 1 && selectedWorkflow) ? selectedWorkflow : undefined,
        stage_id: stageFilter || undefined,
        tag_id: tagFilter || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setRecords(res.data.records);
      setListFields(res.data.list_fields || []);
      setPagination(res.data.pagination);

      // Fetch tags for displayed records
      if (res.data.records.length > 0) {
        try {
          const ids = res.data.records.map(r => r.id);
          const tagData = await tagsApi.batchForRecords(ids);
          setRecordTags(tagData.record_tags || {});
        } catch (e) {}
      } else {
        setRecordTags({});
      }

      // Fetch workflows and tags for filters
      if (rt) {
        try {
          const wfRes = await adminApi.getWorkflows(rt.id);
          const wfs = wfRes.data.workflows || [];
          setWorkflows(wfs);
          if (wfs.length > 0 && !selectedWorkflow) {
            setSelectedWorkflow(wfs[0].id);
          }
        } catch (e) {}
        try {
          const tagsRes = await tagsApi.list(rt.slug);
          setAllTags(tagsRes.tags || []);
        } catch (e) {}
      }
    } catch (err) {
      if (!err.isSessionExpired) toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [typeSlug, page, search, selectedWorkflow, stageFilter, tagFilter, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) fetchData();
      else setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset filters on type change
  useEffect(() => {
    setSearch(''); setStageFilter(''); setTagFilter(''); setPage(1);
    setSortBy('created_at'); setSortDir('desc');
    setSelectedWorkflow(null); setShowFilters(false);
  }, [typeSlug]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };

  // Get stages for selected workflow only
  const currentWorkflow = workflows.find(w => w.id === selectedWorkflow);
  const currentStages = currentWorkflow?.stages || [];

  // Get stage counts from records (all records, not just current page)
  const activeFilterCount = (stageFilter ? 1 : 0) + (tagFilter ? 1 : 0);

  const clearAllFilters = () => {
    setStageFilter('');
    setTagFilter('');
    setPage(1);
  };

  const SortHeader = ({ column, children, className }) => (
    <th onClick={() => handleSort(column)}
      className={`text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 cursor-pointer hover:text-gray-700 select-none ${className || ''}`}>
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column ? (
          sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <div className="h-3 w-3" />
        )}
      </div>
    </th>
  );

  // Find active stage name for display
  const activeStage = stageFilter ? currentStages.find(s => s.id === stageFilter) : null;
  const activeTag = tagFilter ? allTags.find(t => t.id === tagFilter) : null;

  // Smart field detection — contacts show First/Last name columns
  const firstNameField = listFields.find(f => f.name === 'first_name');
  const lastNameField = listFields.find(f => f.name === 'last_name');
  const hasNameFields = !!(firstNameField && lastNameField);

  // Fields to show as columns (exclude first_name/last_name since they get special columns)
  const displayFields = listFields.filter(f =>
    !(hasNameFields && (f.name === 'first_name' || f.name === 'last_name'))
  );

  // Contact type badge styles
  const getTypeStyle = (type) => {
    const styles = {
      'Client': 'bg-blue-50 text-blue-700',
      'Prospect': 'bg-amber-50 text-amber-700',
      'Vendor': 'bg-purple-50 text-purple-700',
      'Partner': 'bg-green-50 text-green-700',
      'Opposing Party': 'bg-red-50 text-red-700',
      'Witness': 'bg-cyan-50 text-cyan-700',
      'Medical Provider': 'bg-emerald-50 text-emerald-700',
      'Insurance Rep': 'bg-indigo-50 text-indigo-700',
      'Attorney': 'bg-violet-50 text-violet-700',
      'Expert': 'bg-teal-50 text-teal-700',
    };
    return styles[type] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{recordType?.name || typeSlug}</h1>
          <p className="text-sm text-gray-500">
            {pagination?.total || 0} record{pagination?.total !== 1 ? 's' : ''}
          </p>
        </div>
        {(!recordType?.requires_intake || currentUser?.is_admin) ? (
          <button onClick={() => navigate('/records/' + typeSlug + '/new')}
            className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
            <Plus className="h-4 w-4" /> New {recordType?.name || 'Record'}
          </button>
        ) : (
          <span className="text-sm text-gray-400 italic">
            Create via Intake → Convert to Case
          </span>
        )}
      </div>

      {/* Workflow Stage Path (Salesforce-style) */}
      {workflows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden">
          {/* Workflow selector tabs */}
          {workflows.length > 1 && (
            <div className="flex border-b border-gray-200 bg-gray-50">
              {workflows.map(wf => (
                <button key={wf.id}
                  onClick={() => { setSelectedWorkflow(wf.id); setStageFilter(''); setPage(1); }}
                  className={'px-4 py-2.5 text-xs font-medium transition-colors relative ' +
                    (selectedWorkflow === wf.id
                      ? 'text-brand-700 bg-white border-b-2 border-brand-600 -mb-[1px]'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100')}>
                  {wf.name}
                </button>
              ))}
            </div>
          )}

          {/* Stage path visualization */}
          {currentStages.length > 0 && (
            <div className="px-4 py-3">
              <div className="flex items-center gap-0.5 overflow-x-auto pb-1 scrollbar-thin">
                {/* All button */}
                <button onClick={() => { setStageFilter(''); setPage(1); }}
                  className={'flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ' +
                    (!stageFilter
                      ? 'bg-gray-800 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100')}>
                  All
                </button>
                <div className="w-px h-4 bg-gray-200 mx-1" />
                {currentStages.map((stage, idx) => {
                  const isActive = stageFilter === stage.id;
                  const isTerminal = stage.settings?.is_terminal;
                  return (
                    <React.Fragment key={stage.id}>
                      <button
                        onClick={() => { setStageFilter(isActive ? '' : stage.id); setPage(1); }}
                        className={'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ' +
                          (isActive
                            ? 'text-white shadow-sm'
                            : 'hover:bg-gray-100')}
                        style={isActive
                          ? { backgroundColor: stage.color }
                          : { color: stage.color }}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: stage.color, opacity: isActive ? 0.7 : 1 }} />
                        {stage.name}
                      </button>
                      {idx < currentStages.length - 1 && !isTerminal && (
                        <ChevronRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search + Filter bar */}
      <div className="flex items-center gap-2 mb-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={'Search ' + (recordType?.name || 'records') + '...'}
              className="flex-1 border-none outline-none text-sm bg-transparent" />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>
        <button onClick={() => setShowFilters(!showFilters)}
          className={'flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ' +
            (activeFilterCount > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50')}>
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="h-5 w-5 flex items-center justify-center bg-brand-600 text-white text-[10px] font-bold rounded-full">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Expanded Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</span>
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters}
                className="text-xs text-red-500 hover:text-red-700 font-medium">
                Clear all
              </button>
            )}
          </div>

          {/* Tag filter as compact chips */}
          {allTags.length > 0 && (
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Tags</label>
              <div className="flex flex-wrap gap-1.5">
                {allTags.map(t => (
                  <button key={t.id} onClick={() => { setTagFilter(tagFilter === t.id ? '' : t.id); setPage(1); }}
                    className="px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    style={tagFilter === t.id
                      ? { backgroundColor: t.color, color: 'white' }
                      : { backgroundColor: t.color + '15', color: t.color }}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterCount > 0 && !showFilters && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-gray-400">Active:</span>
          {activeStage && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full"
              style={{ backgroundColor: activeStage.color + '20', color: activeStage.color }}>
              {activeStage.name}
              <button onClick={() => { setStageFilter(''); setPage(1); }}
                className="hover:opacity-70"><X className="h-3 w-3" /></button>
            </span>
          )}
          {activeTag && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full"
              style={{ backgroundColor: activeTag.color + '20', color: activeTag.color }}>
              <Tag className="h-3 w-3" />{activeTag.name}
              <button onClick={() => { setTagFilter(''); setPage(1); }}
                className="hover:opacity-70"><X className="h-3 w-3" /></button>
            </span>
          )}
          <button onClick={clearAllFilters}
            className="text-xs text-gray-400 hover:text-red-500">Clear all</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : records.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {search || stageFilter || tagFilter ? 'No matching records' : 'No records yet'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {search || stageFilter || tagFilter
              ? 'Try adjusting your search or filters.'
              : 'Create your first ' + (recordType?.name || 'record') + ' to get started.'}
          </p>
          {!(search || stageFilter || tagFilter) && (
            <button onClick={() => navigate('/records/' + typeSlug + '/new')}
              className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
              <Plus className="h-4 w-4" /> Create First Record
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <SortHeader column="reference_number">Ref #</SortHeader>
                  {hasNameFields ? (
                    <>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 cursor-pointer hover:text-gray-700" onClick={() => handleSort('title')}>
                        <div className="flex items-center gap-1">First Name{sortBy === 'title' ? (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <div className="h-3 w-3" />}</div>
                      </th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Last Name</th>
                    </>
                  ) : (
                    <SortHeader column="title">Title</SortHeader>
                  )}
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Tags</th>
                  {displayFields.map(f => (
                    <th key={f.id} className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">{f.label}</th>
                  ))}
                  {currentStages.length > 0 && (
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Stage</th>
                  )}
                  <SortHeader column="status">Status</SortHeader>
                  <SortHeader column="created_at">Created</SortHeader>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((record) => {
                  const tags = recordTags[record.id] || [];
                  return (
                    <tr key={record.id}
                      onClick={() => navigate('/records/' + typeSlug + '/' + record.id)}
                      className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {record.reference_number ? (
                          <button onClick={(e) => { e.stopPropagation(); navigate('/records/' + typeSlug + '/' + record.id); }}
                            className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline">
                            {record.reference_number}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-300">—</span>
                        )}
                      </td>
                      {hasNameFields ? (
                        <>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                            {record.field_data?.[firstNameField?.id] || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                            {record.field_data?.[lastNameField?.id] || '—'}
                          </td>
                        </>
                      ) : (
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[200px] truncate">{record.title}</td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {tags.slice(0, 3).map(tag => (
                            <span key={tag.id} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap"
                              style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                              {tag.name}
                            </span>
                          ))}
                          {tags.length > 3 && (
                            <span className="text-[10px] text-gray-400">+{tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      {displayFields.map(f => (
                        <td key={f.id} className="px-4 py-3 text-sm text-gray-600 max-w-[150px] truncate">
                          {formatFieldValue(record.field_data?.[f.id], f.field_type)}
                        </td>
                      ))}
                      {currentStages.length > 0 && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {record.stage_name && (
                            <span className="text-xs px-2 py-1 rounded-full font-medium"
                              style={{ backgroundColor: (record.stage_color || '#6b7280') + '20', color: record.stage_color || '#6b7280' }}>
                              {record.stage_name}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={'text-xs px-2 py-1 rounded-full font-medium ' + (
                          record.status === 'active' ? 'bg-green-50 text-green-700' :
                          record.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                          'bg-yellow-50 text-yellow-700'
                        )}>{record.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{formatDate(record.created_at)}</td>
                      <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-gray-300" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
                <button onClick={() => setPage(p => p + 1)} disabled={!pagination.has_more}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatFieldValue(value, type) {
  if (value === null || value === undefined) return '—';
  if (type === 'CURRENCY') return '$' + Number(value).toLocaleString();
  if (type === 'DATE') return formatDate(value);
  if (type === 'DATETIME') return formatDateTime(value);
  if (type === 'CHECKBOX' || type === 'TOGGLE') return value ? 'Yes' : 'No';
  if (type === 'MULTI_SELECT' && Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
